/**
 * Local stand-in for the Policy Form's declarations fields (OR-1455).
 *
 * ⚠️ TEMPORARY — delete this module once orion-backend PR #326 deploys.
 *
 * The dec page prints seven fields Orion never captured: the named
 * insured's mailing address, three Coverage B sub-limits, and three
 * Coverage C ones. Without them the generated Policy Document comes out
 * incomplete and the UW fills the gaps by hand — the exact problem
 * OR-1455 exists to close.
 *
 * The backend already models them (`SubmissionQuotePut/Get`,
 * `SubmissionCreate/Update/Get` on the PR #326 branch), but that PR is
 * still in review, so dev's API neither returns nor stores them. This
 * keeps the values in localStorage so the section can be built, filled
 * and reviewed with design today instead of waiting on a merge.
 *
 * The property that makes this safe to leave in place until then: **the
 * wire always wins.** The fallback is applied per field, only where the
 * response carried `null`. The moment the backend starts returning a
 * field, the overlay stops affecting it — no flag flip needed, no stale
 * value shadowing a real one.
 *
 * Note the fallback has to be keyed off the WIRE, not off an empty form
 * value: these fields ship with defaults ($100k / $5k / …), so a missing
 * backend value still arrives as a non-empty string.
 *
 * Scoped per submission id, since these are per-account values.
 */
const STORAGE_KEY = "orion_dec_fields_v1";

/** The seven fields, in the form's own shape (strings, as typed). */
export interface DecFieldsLocal {
    mailingAddress?: string;
    businessDescription?: string;
    fireDamagePerClaimLimit?: string;
    fireDamageAggregateLimit?: string;
    glEachClaimDeductible?: string;
    medicalExpenseLimit?: string;
    evacuationLimit?: string;
    evacuationAggregateLimit?: string;
    evacuationDeductible?: string;
}

/** Form key → quote wire key, for the six Coverage B/C sub-limits. */
export const QUOTE_DEC_FIELDS = {
    fireDamagePerClaimLimit: "fire_damage_per_claim_limit",
    fireDamageAggregateLimit: "fire_damage_aggregate_limit",
    glEachClaimDeductible: "gl_each_claim_deductible",
    medicalExpenseLimit: "medical_expense_limit",
    evacuationLimit: "evacuation_limit",
    evacuationAggregateLimit: "evacuation_aggregate_limit",
    evacuationDeductible: "evacuation_deductible",
} as const;

type Store = Record<string, DecFieldsLocal>;

function readStore(): Store {
    if (typeof window === "undefined") return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? (parsed as Store) : {};
    } catch {
        // Unparseable or unavailable (private mode) — behave as empty.
        return {};
    }
}

/** The locally-held dec fields for a submission. `{}` when there's no
 *  submission id, so callers need no guard of their own. */
export function readDecFields(submissionId: string | undefined): DecFieldsLocal {
    if (!submissionId) return {};
    return readStore()[submissionId] ?? {};
}

/** Merge the caller's fields into whatever is stored for this submission. */
export function writeDecFields(submissionId: string | undefined, values: DecFieldsLocal): void {
    if (!submissionId) return;
    if (typeof window === "undefined") return;
    try {
        const store = readStore();
        store[submissionId] = { ...store[submissionId], ...values };
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Quota or private mode — the field just won't survive a reload.
    }
}

/**
 * Fill in the six quote sub-limits the backend didn't send.
 *
 * `wire` is the raw GET body: a field is overlaid only where it came back
 * `null`/absent, so a real backend value is never overwritten. Mutates
 * and returns `inputs` for convenience at the call site.
 */
export function applyQuoteDecFallback<T extends DecFieldsLocal>(
    inputs: T,
    wire: Partial<Record<(typeof QUOTE_DEC_FIELDS)[keyof typeof QUOTE_DEC_FIELDS], unknown>>,
    local: DecFieldsLocal,
): T {
    for (const [formKey, wireKey] of Object.entries(QUOTE_DEC_FIELDS) as Array<
        [keyof typeof QUOTE_DEC_FIELDS, (typeof QUOTE_DEC_FIELDS)[keyof typeof QUOTE_DEC_FIELDS]]
    >) {
        const stored = local[formKey];
        if (wire[wireKey] == null && stored != null && stored !== "") {
            inputs[formKey] = stored as T[keyof typeof QUOTE_DEC_FIELDS];
        }
    }
    return inputs;
}
